import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Stack, Button, TextField, Table, TableHead,
  TableRow, TableCell, TableBody, TableContainer, Paper,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Chip, Switch, FormControlLabel,
  TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { bgvApi } from '../../../api/bgvApi';
import { BgvVendorDto, BgvVerificationTypeDto } from '../../../types/bgvTypes';

type SortDirection = 'asc' | 'desc';
type VendorSortColumn = 'name' | 'contactPerson' | 'email' | 'login' | 'phone' | 'activeCases' | 'isActive';
type VerificationTypeSortColumn = 'name' | 'description' | 'isActive';

const vendorColumns: Array<{ key: VendorSortColumn; label: string; align?: 'left' | 'center' | 'right' }> = [
  { key: 'name', label: 'Name' },
  { key: 'contactPerson', label: 'Contact Person' },
  { key: 'email', label: 'Email' },
  { key: 'login', label: 'Login' },
  { key: 'phone', label: 'Phone' },
  { key: 'activeCases', label: 'Active Cases' },
  { key: 'isActive', label: 'Status' },
];

const verificationTypeColumns: Array<{ key: VerificationTypeSortColumn; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'description', label: 'Description' },
  { key: 'isActive', label: 'Status' },
];

const normalizeSortValue = (value: unknown): string | number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  const text = value == null || value === '' ? '-' : String(value);
  const numeric = Number(text.replace(/,/g, ''));
  if (text !== '-' && !Number.isNaN(numeric)) return numeric;
  return text.toLowerCase();
};

const BgvVendorManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'vendors' | 'types'>('vendors');
  const [vendors, setVendors] = useState<BgvVendorDto[]>([]);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<BgvVendorDto | null>(null);
  const [vendorForm, setVendorForm] = useState({
    name: '', contactPerson: '', email: '', phone: '', address: '', isActive: true,
  });
  const [vtypes, setVtypes] = useState<BgvVerificationTypeDto[]>([]);
  const [vtDialogOpen, setVtDialogOpen] = useState(false);
  const [vtForm, setVtForm] = useState({ name: '', description: '', isActive: true });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({ contactPerson: false, email: false, phone: false });
  const [vendorSort, setVendorSort] = useState<{ column: VendorSortColumn; direction: SortDirection } | null>(null);
  const [verificationTypeSort, setVerificationTypeSort] = useState<{ column: VerificationTypeSortColumn; direction: SortDirection } | null>(null);

  type VendorForm = { contactPerson: string; email: string; phone: string };

  const [editingVt, setEditingVt] = useState<BgvVerificationTypeDto | null>(null);

  const closeVtDialog = () => {
    setVtDialogOpen(false);
    setEditingVt(null);
    setVtForm({ name: '', description: '', isActive: true });
  };

  const openEditVt = (vt: BgvVerificationTypeDto) => {
    setEditingVt(vt);
    setVtForm({ name: vt.name, description: vt.description || '', isActive: vt.isActive });
    setVtDialogOpen(true);
  };

  const openAddVt = () => {
    setEditingVt(null);
    setVtForm({ name: '', description: '', isActive: true });
    setVtDialogOpen(true);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    const [vendorResult, verificationTypeResult] = await Promise.allSettled([
      bgvApi.getVendors(),
      bgvApi.getVerificationTypes(),
    ]);

    if (vendorResult.status === 'fulfilled') setVendors(vendorResult.value);
    else { setVendors([]); toast.error('Failed to load vendors'); }

    if (verificationTypeResult.status === 'fulfilled') setVtypes(verificationTypeResult.value);
    else { setVtypes([]); toast.error('Failed to load verification types'); }

    setLoading(false);
  }, []);

  useEffect(() => { void loadData(); }, [loadData]);

  const sortedVendors = useMemo(() => {
    if (!vendorSort) return vendors;

    return [...vendors].sort((a, b) => {
      const getValue = (vendor: BgvVendorDto) => {
        if (vendorSort.column === 'login') return vendor.username || vendor.loginEmail || vendor.email || '';
        return normalizeSortValue(vendor[vendorSort.column]);
      };

      const aValue = getValue(a);
      const bValue = getValue(b);
      const compare = typeof aValue === 'number' && typeof bValue === 'number'
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });

      return vendorSort.direction === 'asc' ? compare : -compare;
    });
  }, [vendors, vendorSort]);

  const sortedVerificationTypes = useMemo(() => {
    if (!verificationTypeSort) return vtypes;

    return [...vtypes].sort((a, b) => {
      const aValue = normalizeSortValue(a[verificationTypeSort.column]);
      const bValue = normalizeSortValue(b[verificationTypeSort.column]);
      const compare = typeof aValue === 'number' && typeof bValue === 'number'
        ? aValue - bValue
        : String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' });

      return verificationTypeSort.direction === 'asc' ? compare : -compare;
    });
  }, [vtypes, verificationTypeSort]);

  const handleVendorSort = (column: VendorSortColumn) => {
    setVendorSort((current) => ({
      column,
      direction: current?.column === column && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleVerificationTypeSort = (column: VerificationTypeSortColumn) => {
    setVerificationTypeSort((current) => ({
      column,
      direction: current?.column === column && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleChange = (field: keyof VendorForm, value: string) => {
    let updatedErrors = { ...errors };

    if (field === 'contactPerson') {
      if (/^[A-Za-z\s]*$/.test(value)) {
        updatedErrors.contactPerson = false;
        setVendorForm({ ...vendorForm, contactPerson: value });
      } else {
        updatedErrors.contactPerson = true;
      }
    }

    if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      updatedErrors.email = !emailRegex.test(value);
      setVendorForm({ ...vendorForm, email: value });
    }

    if (field === 'phone') {
      if (/^\d{0,10}$/.test(value)) {
        updatedErrors.phone = value.length !== 10;
        setVendorForm({ ...vendorForm, phone: value });
      } else {
        updatedErrors.phone = true;
      }
    }

    setErrors(updatedErrors);
  };

  const openAddVendor = () => {
    setEditingVendor(null);
    setVendorForm({ name: '', contactPerson: '', email: '', phone: '', address: '', isActive: true });
    setVendorDialogOpen(true);
  };

  const openEditVendor = (v: BgvVendorDto) => {
    setEditingVendor(v);
    setVendorForm({
      name: v.name, contactPerson: v.contactPerson || '', email: v.email || '',
      phone: v.phone || '', address: v.address || '', isActive: v.isActive,
    });
    setVendorDialogOpen(true);
  };

  const deleteVendor = async (vendorId: string) => {
    debugger;
  const confirmed = window.confirm(
    'Are you sure you want to delete this vendor?'
  );

  if (!confirmed) return;

  try {
    await bgvApi.deletevendors(vendorId);

    setVendors((prev) =>
      prev.filter((v) => v.id !== vendorId)
    );
    loadData();
    toast.success('Vendor deleted successfully');
  } catch (error) {
    console.error(error);
    toast.error('Failed to delete vendor');
  }
};

const deleteVerification = async (VerificationId: string) => {
  const confirmed = window.confirm(
    'Are you sure you want to delete this verification type?'
  );

  if (!confirmed) return;

  try {
    await bgvApi.deleteVerificationType(VerificationId);

    setVendors((prev) =>
      prev.filter((v) => v.id !== VerificationId)
    );
    loadData();
    toast.success('Verification type deleted successfully');
  } catch (error) {
    console.error(error);
    toast.error('Failed to delete vendor');
  }
};
  
  const handleSaveVendor = async () => {
    if (!vendorForm.name.trim()) { toast.warning('Vendor name is required'); return; }
    if (!vendorForm.email.trim()) { toast.warning('Vendor email is required'); return; }

    setSaving(true);
    try {
      if (editingVendor) {
        await bgvApi.updateVendor(editingVendor.id, { id: editingVendor.id, ...vendorForm });
        toast.success('Vendor updated');
      } else {
        await bgvApi.createVendor(vendorForm);
        toast.success('Vendor created and credentials sent');
      }
      setVendorDialogOpen(false);
      await loadData();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVt = async () => {
    if (!vtForm.name.trim()) { toast.warning('Name is required'); return; }
    setSaving(true);
    try {
      if (editingVt) {
        await bgvApi.updateVerificationType(editingVt.id, { id: editingVt.id, ...vtForm });
        toast.success('Verification type updated');
      } else {
        await bgvApi.createVerificationType(vtForm);
        toast.success('Verification type created');
      }
      closeVtDialog();
      await loadData();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || `Failed to ${editingVt ? 'update' : 'create'} verification type`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={3}>BGV Settings</Typography>

      <Stack direction="row" spacing={1} mb={3}>
        <Button variant={activeTab === 'vendors' ? 'contained' : 'outlined'} onClick={() => setActiveTab('vendors')} sx={{ textTransform: 'none' }}>
          Vendors
        </Button>
        <Button variant={activeTab === 'types' ? 'contained' : 'outlined'} onClick={() => setActiveTab('types')} sx={{ textTransform: 'none' }}>
          Verification Types
        </Button>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh"><IconButton onClick={loadData}><RefreshIcon /></IconButton></Tooltip>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress /></Box>
      ) : activeTab === 'vendors' ? (
        <>
          <Stack direction="row" justifyContent="flex-end" mb={2}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddVendor} sx={{ textTransform: 'none' }}>
              Add Vendor
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Total Records: {vendors.length}</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
            <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: '#F9FAFB',
                      '& .MuiTableCell-root': {
                        fontWeight: 700,
                        color: '#374151'
                      }
                    }}
                  >
                    {vendorColumns.map((column) => (
                      <TableCell key={column.key}>
                        <TableSortLabel
                          active={vendorSort?.column === column.key}
                          direction={vendorSort?.column === column.key ? vendorSort.direction : 'asc'}
                          onClick={() => handleVendorSort(column.key)}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {vendors.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No vendors found</Typography></TableCell></TableRow>
                ) : sortedVendors.map((v) => (
                  <TableRow key={v.id} hover>
                    <TableCell><Typography fontWeight={600}>{v.name}</Typography></TableCell>
                    <TableCell>{v.contactPerson || '-'}</TableCell>
                    <TableCell>{v.email || '-'}</TableCell>
                    <TableCell>
                      {v.hasLoginCredentials ? (
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600}>{v.username || 'Vendor user'}</Typography>
                          <Typography variant="caption" color="text.secondary">{v.loginEmail || v.email || '-'}</Typography>
                        </Stack>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{v.phone || '-'}</TableCell>
                    <TableCell>{v.activeCases}</TableCell>
                    <TableCell><Chip label={v.isActive ? 'Active' : 'Inactive'} color={v.isActive ? 'success' : 'default'} size="small" /></TableCell>
                      {/* <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => openEditVendor(v)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => deleteVendor(v.id)}
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip> */}

                    <TableCell align="center">
                      <Stack direction="row" spacing={0.75} justifyContent="center">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEditVendor(v)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton  color="error" size="small" onClick={() => deleteVendor(v.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                    {/* </TableCell> */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <>
          <Stack direction="row" justifyContent="flex-end" mb={2}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddVt} sx={{ textTransform: 'none' }}>
              Add Verification Type
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Total Records: {vtypes.length}</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: '#F9FAFB',
                    '& .MuiTableCell-root': {
                      fontWeight: 700,
                      color: '#374151'
                    }
                  }}
                >
                  {verificationTypeColumns.map((column) => (
                    <TableCell key={column.key}>
                      <TableSortLabel
                        active={verificationTypeSort?.column === column.key}
                        direction={verificationTypeSort?.column === column.key ? verificationTypeSort.direction : 'asc'}
                        onClick={() => handleVerificationTypeSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vtypes.length === 0 ? (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}><Typography color="text.secondary">No verification types found</Typography></TableCell></TableRow>
                ) : sortedVerificationTypes.map((vt) => (
                  <TableRow key={vt.id} hover>
                    <TableCell><Typography fontWeight={600}>{vt.name}</Typography></TableCell>
                    <TableCell>{vt.description || '-'}</TableCell>
                    <TableCell><Chip label={vt.isActive ? 'Active' : 'Inactive'} color={vt.isActive ? 'success' : 'default'} size="small" /></TableCell>
                    {/* <TableCell>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditVt(vt)}>
                        <EditIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteVerification(vt.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell> */}
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.75} justifyContent="center">
                      <Tooltip title="Edit"><IconButton  size="small" onClick={() => openEditVt(vt)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton  color="error" size="small" onClick={() => deleteVerification(vt.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Vendor Dialog */}
      <Dialog open={vendorDialogOpen} onClose={() => setVendorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingVendor ? 'Edit' : 'Add'} Vendor</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Vendor Name" required fullWidth value={vendorForm.name}
              onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })} />
            <TextField label="Contact Person" fullWidth value={vendorForm.contactPerson}
              error={errors.contactPerson} helperText={errors.contactPerson ? 'Only alphabets allowed' : ''}
              onChange={(e) => handleChange('contactPerson', e.target.value)} />
            <TextField label="Email" type="email" required fullWidth value={vendorForm.email}
              error={errors.email} helperText={errors.email ? 'Enter valid email (e.g., abc@gmail.com)' : ''}
              onChange={(e) => handleChange('email', e.target.value)} />
            <TextField
              label="Phone"
              fullWidth
              value={vendorForm.phone}
              error={vendorForm.phone.length > 0 && vendorForm.phone.length !== 10}
              helperText={
                vendorForm.phone.length > 0 && vendorForm.phone.length !== 10
                  ? 'Enter 10-digit number'
                  : ''
              }
              inputProps={{ maxLength: 10 }}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                handleChange('phone', value);
              }}
            />
            <TextField label="Address" multiline rows={2} fullWidth value={vendorForm.address}
              onChange={(e) => setVendorForm({ ...vendorForm, address: e.target.value })} />
            {editingVendor && (
              <FormControlLabel
                control={<Switch checked={vendorForm.isActive} onChange={(e) => setVendorForm({ ...vendorForm, isActive: e.target.checked })} />}
                label="Active"
              />
            )}
            {!editingVendor && (
              <Typography variant="caption" color="text.secondary">
                A vendor login will be created automatically and the credentials will be emailed to this vendor.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVendorDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveVendor} disabled={saving} sx={{ textTransform: 'none' }}>
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verification Type Dialog */}
      <Dialog open={vtDialogOpen} onClose={closeVtDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingVt ? 'Edit' : 'Add'} Verification Type</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" required fullWidth value={vtForm.name}
              onChange={(e) => setVtForm({ ...vtForm, name: e.target.value })} />
            <TextField label="Description" multiline rows={2} fullWidth value={vtForm.description}
              onChange={(e) => setVtForm({ ...vtForm, description: e.target.value })} />
            <FormControlLabel
              control={
                <Switch
                  checked={vtForm.isActive}
                  onChange={(e) => setVtForm({ ...vtForm, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVtDialog} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveVt} disabled={saving} sx={{ textTransform: 'none' }}>
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BgvVendorManagementPage;
